import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import loadingAnimation from "@assets/loading_anim.json";
import smthWrong from "/smth_wrong.svg";
import { useReportGenerationWebSocket } from "@pages/Report/utils/useReportGenerationWebSocket";
import LoadingBar from "@shared/components/LoadingBar";
import { useProgressWithNavigation } from "./utils/useProgressWithNavigation";
import Loader from "@shared/components/Loader";

const ReportProcessing: React.FC<{ taskId: string; projectId: string }> = ({
  taskId,
  projectId,
}) => {
  const { reportStatus, progressMessage, reportId } =
    useReportGenerationWebSocket({ taskId });

  const [dots, setDots] = useState<number>(1);
  const navigate = useNavigate();

  const progress = useProgressWithNavigation({
    progressMessage,
    reportStatus,
    reportId,
    projectId,
    allowNavigation: true,
    taskId,
  });

  // Animate dots (...)
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev % 3) + 1);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-screen pb-32 scale-90 2xl:scale-100">
      {progress >= 100 ? (
        <div className="mt-8" />
      ) : (
        <div className="flex flex-col items-center">
          {["PROGRESS", "PENDING", "SUCCESS"].includes(reportStatus) ? (
            <>
              <div className="mb-8">
                <Lottie
                  animationData={loadingAnimation}
                  style={{ width: 350 }}
                />
              </div>
              <div className="w-[500px]">
                <h1 className="text-lg font-semibold mb-4">
                  Just a moment, your report is being generated
                  {".".repeat(dots)}
                </h1>
                <LoadingBar
                  progress={progress}
                  message={
                    reportStatus === "SUCCESS"
                      ? "Completed ✅"
                      : reportStatus === "PENDING"
                      ? "Pending"
                      : progressMessage
                  }
                />
              </div>
            </>
          ) : reportStatus === "FAILURE" ? (
            <>
              <img
                src={smthWrong}
                alt="somethingWentWrong"
                style={{ width: "500px", height: "500px" }}
              />
              <h1 className="font-bold">Something went wrong</h1>
              <h2>
                Try to choose another file or template. If the issue persists,
                contact support.
              </h2>
              <button
                className="bg-blue-500 border-blue-500 border-2 text-white rounded-md mt-4 py-2 px-4 hover:bg-white hover:text-blue-500 transition"
                onClick={() => navigate(`/project/${projectId}`)}
              >
                Try again
              </button>
            </>
          ) : (
            <Loader color="#184CD8" size="lg" />
          )}
        </div>
      )}
    </div>
  );
};

const ReportProcessingPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const { projectId } = useParams<{ projectId: string }>();

  return <ReportProcessing taskId={taskId || ""} projectId={projectId || ""} />;
};

export default ReportProcessingPage;
